import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiBookOpen, FiCalendar, FiCheck, FiClock, FiRefreshCw, FiUser } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatUtcDateTimeToChicago } from '../../utils/timezoneUtils';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import styles from './MyAttendance.module.css';
import { DateTime } from 'luxon';
import { getCheckedInTimestamp, hasCheckedInRecord } from '../../utils/attendanceSessionStatus';

function MyAttendance({ autoCheckIn = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [info, setInfo] = useState('');
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [courses, setCourses] = useState([]);
  const [reportsByCourse, setReportsByCourse] = useState({});
  const [loadingReports, setLoadingReports] = useState(false);
  const [showPreviousCourses, setShowPreviousCourses] = useState(false);
  const [reportError, setReportError] = useState('');
  const [expandedReports, setExpandedReports] = useState({});

  const autoProcessedRef = useRef(false);

  useEffect(() => {
    if (!authLoading && !user) {
      const redirectPath = `${location.pathname}${location.search || ''}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`, { replace: true });
    }
  }, [authLoading, user, navigate, location.pathname, location.search]);

  const applyFlashState = useCallback(() => {
    const flash = location.state?.flash;
    if (!flash) return;

    if (flash.type === 'success') {
      setSuccess(flash.message || 'Attendance updated.');
      setError('');
      setInfo('');
    } else if (flash.type === 'error') {
      setError(flash.message || 'Attendance check-in failed.');
      setSuccess('');
      setInfo('');
    } else {
      setInfo(flash.message || 'Attendance status updated.');
      setError('');
      setSuccess('');
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    applyFlashState();
  }, [applyFlashState]);

  const fetchSessions = useCallback(async () => {
    if (!user || user.role !== 'student') {
      setSessions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await apiService.getActiveAttendanceSessionsToday();
      if (response?.success) {
        setSessions(Array.isArray(response.sessions) ? response.sessions : []);
      } else {
        setError(response?.message || 'Failed to load attendance sessions.');
      }
    } catch (e) {
      logger.error('MyAttendance fetch sessions failed', { error: e });
      setError(e?.message || 'Failed to load attendance sessions.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const fetchCoursesForReport = useCallback(async () => {
    if (!user || user.role !== 'student') {
      return;
    }

    try {
      const response = await apiService.getCourses();
      if (response?.success && Array.isArray(response.courses)) {
        setCourses(response.courses);
      } else {
        setCourses([]);
      }
    } catch (e) {
      logger.error('MyAttendance fetch courses failed', { error: e });
      setCourses([]);
    }
  }, [user]);

  useEffect(() => {
    fetchCoursesForReport();
  }, [fetchCoursesForReport]);

  const checkInSession = useCallback(async (sessionId, { silent = false } = {}) => {
    try {
      setCheckingIn(prev => new Set(prev).add(sessionId));
      if (!silent) {
        setError('');
        setSuccess('');
        setInfo('');
      }

      const response = await apiService.checkInAttendance(sessionId, 'present');
      if (response?.success) {
        if (!silent) {
          setSuccess('Successfully checked in for attendance.');
        }
        await fetchSessions();
        return { success: true, response };
      }

      const msg = response?.message || 'Failed to check in for attendance.';
      if (!silent) {
        setError(msg);
      }
      return { success: false, message: msg };
    } catch (e) {
      logger.error('MyAttendance check-in failed', { error: e, sessionId });
      const msg = e?.message || 'Failed to check in for attendance.';
      if (!silent) {
        setError(msg);
      }
      return { success: false, message: msg };
    } finally {
      setCheckingIn(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  }, [fetchSessions]);

  const visibleSessions = useMemo(() => {
    return (sessions || []).slice().sort((a, b) => {
      const aTime = a?.start_time ? new Date(a.start_time).getTime() : 0;
      const bTime = b?.start_time ? new Date(b.start_time).getTime() : 0;
      return aTime - bTime;
    });
  }, [sessions]);

  const reportCourses = useMemo(() => {
    const source = Array.isArray(courses) ? courses : [];
    return source.filter(course => {
      const isActive = course.is_active === 1 || course.is_active === true;
      return showPreviousCourses ? true : isActive;
    });
  }, [courses, showPreviousCourses]);

  const fetchAttendanceReports = useCallback(async () => {
    if (!user || user.role !== 'student') {
      return;
    }

    if (!reportCourses.length) {
      setReportsByCourse({});
      setReportError('');
      return;
    }

    try {
      setLoadingReports(true);
      setReportError('');

      const results = await Promise.all(
        reportCourses.map(async (course) => {
          try {
            const response = await apiService.getStudentAttendance(course.id);
            const stats = response?.attendance?.statistics || null;
            const history = Array.isArray(response?.attendance?.history) ? response.attendance.history : [];

            return [
              course.id,
              {
                course,
                statistics: stats,
                history,
                error: null
              }
            ];
          } catch (e) {
            return [
              course.id,
              {
                course,
                statistics: null,
                history: [],
                error: e?.message || 'Failed to load report'
              }
            ];
          }
        })
      );

      setReportsByCourse(Object.fromEntries(results));
    } catch (e) {
      logger.error('MyAttendance fetch attendance reports failed', { error: e });
      setReportError('Failed to load attendance reports.');
    } finally {
      setLoadingReports(false);
    }
  }, [reportCourses, user]);

  useEffect(() => {
    fetchAttendanceReports();
  }, [fetchAttendanceReports]);

  useEffect(() => {
    if (!autoCheckIn || autoProcessedRef.current || loading) {
      return;
    }

    autoProcessedRef.current = true;

    const executeAutoFlow = async () => {
      if (visibleSessions.length === 0) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'info', message: 'No active attendance sessions are available right now.' } }
        });
        return;
      }

      if (visibleSessions.length > 1) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'info', message: 'Multiple sessions are available. Select one below to check in.' } }
        });
        return;
      }

      const onlySession = visibleSessions[0];
      const alreadyCheckedIn = hasCheckedInRecord(onlySession);

      if (alreadyCheckedIn) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'success', message: 'You are already checked in for today\'s active session.' } }
        });
        return;
      }

      if (!isWithinCheckinWindow(onlySession)) {
        navigate(`/attendance/checkin/${onlySession.session_id}`, { replace: true });
        return;
      }

      const result = await checkInSession(onlySession.session_id, { silent: true });

      if (result.success) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'success', message: 'Auto check-in completed successfully.' } }
        });
      } else {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'error', message: result.message || 'Auto check-in failed.' } }
        });
      }
    };

    executeAutoFlow();
  }, [autoCheckIn, checkInSession, loading, navigate, visibleSessions]);

  const getStateChipClass = (session) => {
    if (session?.isActive) return `${styles.chip} ${styles.chipActive}`;
    if (session?.isFuture) return `${styles.chip} ${styles.chipFuture}`;
    return `${styles.chip} ${styles.chipPast}`;
  };

  const getStateLabel = (session) => {
    if (session?.isActive) return 'Active now';
    if (session?.isFuture) return 'Upcoming';
    return 'Ended';
  };

  const getEntityName = (session) => {
    return session?.course_name || session?.elective_name || session?.title || 'Attendance Session';
  };

  const formatDateLabel = (iso) => {
    if (!iso) return 'Date unavailable';

    // Date-only values (and DATE columns serialized at UTC midnight) should not shift by timezone.
    if (typeof iso === 'string') {
      const dateOnlyLike = iso.match(/^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.000)?Z?)?$/);
      if (dateOnlyLike) {
        const dateOnly = DateTime.fromISO(dateOnlyLike[1], { zone: 'America/Chicago' });
        return dateOnly.isValid ? dateOnly.toFormat('cccc, LLL d, yyyy') : 'Date unavailable';
      }
    }

    if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const dateOnly = DateTime.fromISO(iso, { zone: 'America/Chicago' });
      return dateOnly.isValid ? dateOnly.toFormat('cccc, LLL d, yyyy') : 'Date unavailable';
    }

    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone('America/Chicago');
    return dt.isValid ? dt.toFormat('cccc, LLL d, yyyy') : formatUtcDateTimeToChicago(iso);
  };

  const formatTimeLabel = (iso) => {
    if (!iso) return '--';
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone('America/Chicago');
    return dt.isValid ? dt.toFormat('h:mm a') : '--';
  };

  const formatCheckedInTimeLabel = (value) => {
    if (!value) return null;

    const fromIso = DateTime.fromISO(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromIso.isValid) return fromIso.toFormat('h:mm a');

    const fromSql = DateTime.fromSQL(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromSql.isValid) return fromSql.toFormat('h:mm a');

    return null;
  };

  const formatCheckedInDateTimeLabel = (value) => {
    if (!value) return null;

    const fromIso = DateTime.fromISO(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromIso.isValid) return fromIso.toFormat('ccc, LLL d, h:mm a');

    const fromSql = DateTime.fromSQL(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromSql.isValid) return fromSql.toFormat('ccc, LLL d, h:mm a');

    return null;
  };

  const isWithinCheckinWindow = (session) => {
    if (!session) return false;

    const nowChicago = DateTime.now().setZone('America/Chicago');
    const timeRegex = /^\d{2}:\d{2}(:\d{2})?$/;
    const checkinStart = session?.checkin_start_time || null;
    const checkinEnd = session?.checkin_end_time || null;

    if (typeof checkinStart === 'string' && typeof checkinEnd === 'string' && timeRegex.test(checkinStart) && timeRegex.test(checkinEnd)) {
      const nowClock = nowChicago.toFormat('HH:mm:ss');
      const startClock = checkinStart.length === 5 ? `${checkinStart}:00` : checkinStart;
      const endClock = checkinEnd.length === 5 ? `${checkinEnd}:00` : checkinEnd;
      return nowClock >= startClock && nowClock <= endClock;
    }

    const start = session?.start_time ? DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago') : null;
    const end = session?.end_time ? DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago') : null;

    if (start?.isValid && end?.isValid) {
      return nowChicago >= start && nowChicago <= end;
    }

    return true;
  };

  if (!user && !authLoading) {
    return null;
  }

  return (
    <div className={styles.container}>
      <section className={styles.header}>
        <div className={styles.titleWrap}>
          <h1>My Attendance</h1>
          <p className={styles.subtitle}>Check in to today&apos;s active attendance sessions.</p>
        </div>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={fetchSessions}
          disabled={loading}
        >
          <FiRefreshCw size={14} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </section>

      <section className={styles.activeSection}>
        <div className={styles.reportHeader}>
          <h2 className={styles.reportTitle}>Active Sessions</h2>
        </div>

        {loading ? (
          <div className={styles.empty}>Loading attendance sessions...</div>
        ) : visibleSessions.length === 0 ? (
          <div className={styles.empty}>No attendance sessions are available for check-in right now.</div>
        ) : (
          visibleSessions.map((session) => {
            const alreadyCheckedIn = hasCheckedInRecord(session);
            const checkedInTimeLabel = formatCheckedInTimeLabel(getCheckedInTimestamp(session));
            const disabled = checkingIn.has(session.session_id) || alreadyCheckedIn || session?.isPast;

            return (
              <article className={styles.card} key={session.session_id}>
                <div className={styles.metaRow}>
                  <div className={`${styles.metaItem} ${styles.metaTitleItem}`}>
                    <FiBookOpen size={14} />
                    <span className={styles.metaTitleText}>{session?.title || getEntityName(session)}</span>
                  </div>
                  <span className={styles.metaSeparator}>•</span>
                  <div className={styles.metaItem}>
                    <FiCalendar size={14} />
                    {formatDateLabel(session.start_time)}
                  </div>
                  <span className={styles.metaSeparator}>•</span>
                  <div className={styles.metaItem}>
                    <FiUser size={14} />
                    {session?.instructors || `${session?.instructor_first_name || ''} ${session?.instructor_last_name || ''}`.trim() || 'Instructor'}
                  </div>
                  <span className={styles.metaSeparator}>•</span>
                  <div className={styles.metaItem}>
                    <FiBookOpen size={14} />
                    {session?.entity_type === 'elective' ? 'Elective' : 'Course'}
                    {session?.course_name || session?.elective_name ? ` ${session.course_name || session.elective_name}` : ''}
                  </div>
                  <span className={`${getStateChipClass(session)} ${styles.metaStatusChip}`}>{getStateLabel(session)}</span>
                </div>

                <div className={styles.timeWindow}>
                  <div className={styles.timeWindowText}>
                    <FiClock size={14} />
                    <span className={styles.timeWindowLabel}>Check-in window:</span>
                    <span className={styles.timeWindowValue}>
                      {formatTimeLabel(session.start_time)} - {formatTimeLabel(session.end_time)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.checkInBtn} ${alreadyCheckedIn ? styles.checkInBtnSuccess : ''}`}
                    disabled={disabled}
                    onClick={() => checkInSession(session.session_id)}
                  >
                    <FiCheck size={14} />
                    {checkingIn.has(session.session_id)
                      ? 'Checking in...'
                      : alreadyCheckedIn
                        ? checkedInTimeLabel
                          ? `Checked In • ${checkedInTimeLabel}`
                          : 'Checked In'
                        : session?.isPast
                          ? 'Check In Closed'
                          : 'Check In'}
                  </button>
                </div>
              </article>
            );
          })
        )}
      </section>

      {success && <div className={`${styles.status} ${styles.statusSuccess}`}>{success}</div>}
      {info && <div className={`${styles.status} ${styles.statusInfo}`}>{info}</div>}
      {error && <div className={`${styles.status} ${styles.statusError}`}>{error}</div>}

      <section className={styles.reportSection}>
        <div className={styles.reportHeader}>
          <h2 className={styles.reportTitle}>Attendance Statistics</h2>
          <button
            type="button"
            className={styles.reportToggle}
            onClick={() => setShowPreviousCourses(prev => !prev)}
          >
            {showPreviousCourses ? 'Hide Previous Courses' : 'Show Previous Courses'}
          </button>
        </div>

        {reportError && <div className={`${styles.status} ${styles.statusError}`}>{reportError}</div>}

        {loadingReports ? (
          <div className={styles.empty}>Loading attendance reports...</div>
        ) : reportCourses.length === 0 ? (
          <div className={styles.empty}>No courses available for attendance reporting.</div>
        ) : (
          <div className={styles.reportList}>
            {reportCourses.map((course) => {
              const report = reportsByCourse[course.id];
              const stats = report?.statistics;
              const history = report?.history || [];
              const isActive = course.is_active === 1 || course.is_active === true;
              const isExpanded = Boolean(expandedReports[course.id]);

              return (
                <article key={course.id} className={styles.reportCard}>
                  <div className={styles.reportCardHeader}>
                    <div>
                      <h3 className={styles.reportCourseName}>{course.name}</h3>
                      <p className={styles.reportCourseMeta}>{course.code}</p>
                    </div>
                    <span className={`${styles.reportStatusBadge} ${isActive ? styles.reportStatusActive : styles.reportStatusPrevious}`}>
                      {isActive ? 'Active' : 'Previous'}
                    </span>
                  </div>

                  {report?.error ? (
                    <div className={`${styles.status} ${styles.statusError}`}>{report.error}</div>
                  ) : stats ? (
                    <>
                      <div className={styles.reportStats}>
                        <div className={styles.reportStatItem}>
                          <span className={styles.reportStatLabel}>Total Sessions</span>
                          <strong className={styles.reportStatValue}>{stats.totalSessions ?? 0}</strong>
                        </div>
                        <div className={styles.reportStatItem}>
                          <span className={styles.reportStatLabel}>Present</span>
                          <strong className={styles.reportStatValue}>{stats.presentCount ?? 0}</strong>
                        </div>
                        <div className={styles.reportStatItem}>
                          <span className={styles.reportStatLabel}>Absent</span>
                          <strong className={styles.reportStatValue}>{stats.absentCount ?? 0}</strong>
                        </div>
                        <div className={styles.reportStatItem}>
                          <span className={styles.reportStatLabel}>Attendance Rate</span>
                          <strong className={styles.reportStatValue}>{stats.attendanceRate ?? 0}%</strong>
                        </div>
                      </div>

                      <div className={styles.reportDetailsRow}>
                        <button
                          type="button"
                          className={styles.reportDetailsToggle}
                          onClick={() => setExpandedReports(prev => ({
                            ...prev,
                            [course.id]: !prev[course.id]
                          }))}
                        >
                          {isExpanded ? 'Hide Session Details' : 'Show Session Details'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className={styles.reportDetailsList}>
                          {history.length === 0 ? (
                            <div className={styles.empty}>No session-level attendance records found.</div>
                          ) : (
                            history.map((entry) => {
                              const statusValue = entry?.status || 'unknown';
                              const checkedInTimeLabel = statusValue === 'present'
                                ? formatCheckedInDateTimeLabel(entry?.last_checkin || entry?.checked_in_at_utc || entry?.updated_at)
                                : null;
                              const statusClass = statusValue === 'present'
                                ? styles.reportSessionStatusPresent
                                : statusValue === 'absent'
                                  ? styles.reportSessionStatusAbsent
                                  : styles.reportSessionStatusNeutral;

                              return (
                                <div key={`${course.id}-${entry.session_id}`} className={styles.reportSessionItem}>
                                  <div className={styles.reportSessionMain}>
                                    <div className={styles.reportSessionTitle}>{entry.title || 'Session'}</div>
                                    <div className={styles.reportSessionRight}>
                                      <div className={styles.reportSessionMeta}>
                                        {formatDateLabel(entry.date)}
                                        {checkedInTimeLabel ? ` • Marked present on ${checkedInTimeLabel}` : ''}
                                      </div>
                                      <span className={`${styles.reportSessionStatus} ${statusClass}`}>
                                        {statusValue}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.empty}>No attendance data available for this class.</div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default MyAttendance;
